import React, { useState } from 'react';
import axios from 'axios';
import { observer } from 'mobx-react-lite';
import DynamicTableCSVReport from '../DynamicTable/DynamicTableCSVReport';
import { Select } from 'semantic-ui-react';

// Format the date to MM-DD-YYYY
export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

// Backend data structure
interface DataItem {
  Flim_Title: string;
  Pl_Name: string;
  Emp_Id: string;
  Emp_Name: string;
  Reel_Name: string;
  Shot_Name: string;
  work_Type: string;
  Total_Frame: number;
  Actual_Hours: string;
  Extra_Hours: string;
  Total_Reject_Frame: number;
  startDate: string;
  endDate: string;
}

type SearchKey = keyof DataItem;

const OperatorPerformanceNFAIReport: React.FC = () => {
  const today = new Date().toISOString().split('T')[0];
  const [data, setData] = useState<DataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [location,setLocation] = useState<{selected:string, options:any[]}>({ selected:"", options: [
    { key: '0', text: 'Chennai', value: 'Chennai' },
    { key: '1', text: 'Hyderabad', value: 'Hyderabad' },
  ] });

  // Dynamic search filters
  const [filters, setFilters] = useState<Array<{ searchName: SearchKey | ''; searchValue: string }>>([
    { searchName: '', searchValue: '' }
  ]);

  // Field options
  const searchFields: SearchKey[] = [
    'Flim_Title', 'Pl_Name', 'Emp_Id', 'Emp_Name',
    'Reel_Name', 'Shot_Name', 'work_Type',
    'Total_Frame', 'Actual_Hours', 'Extra_Hours',
    'Total_Reject_Frame', 'startDate', 'endDate'
  ];

  // Fetch data from backend
  const fetchData = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);

    try {
      const startDateFormatted = formatDate(startDate);
      const endDateFormatted = formatDate(endDate);

      const searchQuery = filters
        .filter(f => f.searchName && f.searchValue)
        .map(f => `${f.searchName}:${f.searchValue}`)
        .join(',');

      const params = new URLSearchParams();
      params.append('fromDate', startDateFormatted);
      params.append('toDate', endDateFormatted);
      if (searchQuery) params.append('search', searchQuery);
      params.append('location', location.selected);

      const response = await axios.get(`/Hour/detailsForNFAIDate?${params.toString()}`);
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(axios.isAxiosError(err) ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Clear filters and reload data
  const clearSearch = () => {
    setFilters([{ searchName: '', searchValue: '' }]);
    fetchData();
  };

  // Styles
  const styles = {
    container: {
      maxWidth: '95%', margin: 'auto', padding: '20px',
      backgroundColor: '#fff', borderRadius: '12px',
      boxShadow: '0 0 15px rgba(0,0,0,0.1)', overflowX: 'auto',
      width: '99vw', height: '90vh',
    } as React.CSSProperties,

    filterBox: {
      marginBottom: '20px', textAlign: 'center', display: 'flex',
      justifyContent: 'center', alignItems: 'center', gap: '20px',
      flexWrap: 'wrap', border: '1px solid #ccc', padding: '20px',
      borderRadius: '12px', backgroundColor: '#f4f8f8',
    } as React.CSSProperties,

    input: {
      padding: '10px 14px', border: '2px solid #00796b',
      borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff',
      color: '#333', width: '220px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    } as React.CSSProperties,

    select: {
      width: '180px',
      padding: '10px 14px', border: '2px solid #00796b',
      borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff',
      color: '#333', boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    } as React.CSSProperties,

    button: {
      padding: '10px 20px', backgroundColor: '#00796b',
      color: '#fff', border: 'none', borderRadius: '6px',
      fontWeight: 'bold', cursor: 'pointer', fontSize: '14px',
    } as React.CSSProperties,

    loader: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    color: '#25b09b',
    animation: 'spinLoader 1s infinite steps(8)',
    position: 'relative',
    '--d': '22px' as any, // TypeScript quirk for custom properties
    boxShadow: `
      calc(1 * var(--d))      calc(0 * var(--d))      0 0,
      calc(0.707 * var(--d))  calc(0.707 * var(--d))  0 1px,
      calc(0 * var(--d))      calc(1 * var(--d))      0 2px,
      calc(-0.707 * var(--d)) calc(0.707 * var(--d))  0 3px,
      calc(-1 * var(--d))     calc(0 * var(--d))      0 4px,
      calc(-0.707 * var(--d)) calc(-0.707 * var(--d)) 0 5px,
      calc(0 * var(--d))      calc(-1 * var(--d))     0 6px
    `
  }

  };

  return (
    <div style={styles.container}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}><b>Operator Performance Report</b></h2>

      {/* Date Filters */}
      <div style={styles.filterBox}>
        <div>
          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Start Date:</label>
          <input type="date" value={startDate} max={today}
            onChange={e => setStartDate(e.target.value)} style={styles.input} />
        </div>

        <div>
          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>End Date:</label>
          <input type="date" value={endDate} max={today}
            onChange={e => setEndDate(e.target.value)} style={styles.input} />
        </div>

        <div>
          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Location:</label>
          <Select
            style={styles.select}
            value={location.selected}
            onChange={(_e: any, data: any) =>
              setLocation((prev) => ({
                ...prev,
                selected: data.value as string,
              }))
            }
            options={location.options}
            placeholder={'Select location'}
            search
            clearable
            />
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={fetchData} style={styles.button}>Search</button>
          <button onClick={clearSearch} style={{ ...styles.button, backgroundColor: '#888' }}>Clear Search</button>
        </div>
      </div>

      {/* Search Filters */}
      {data.length > 0 && (
        <div style={{ ...styles.filterBox, marginBottom: '15px' }}>
          {filters.map((filter, index) => (
            <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <select
                value={filter.searchName}
                onChange={e =>
                  setFilters(prev =>
                    prev.map((f, i) =>
                      i === index ? { ...f, searchName: e.target.value as SearchKey } : f
                    )
                  )
                }
                style={styles.select}
              >
                <option value="">Select Field</option>
                {searchFields.map(field => (
                  <option key={field} value={field}>
                    {field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={filter.searchValue}
                onChange={e =>
                  setFilters(prev =>
                    prev.map((f, i) =>
                      i === index ? { ...f, searchValue: e.target.value } : f
                    )
                  )
                }
                placeholder="Search value"
                style={styles.input}
              />

              {index > 0 && (
                <button
                  onClick={() => setFilters(prev => prev.filter((_, i) => i !== index))}
                  style={{ ...styles.button, backgroundColor: '#d32f2f' }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          <button
            onClick={() => setFilters([...filters, { searchName: '', searchValue: '' }])}
            style={{ ...styles.button, backgroundColor: '#26796b' }}
          >
            + Add Filter
          </button>
        </div>
      )}

      {/* CSV Export */}
      {data.length > 0 && (
        <div style={{ textAlign: 'right', paddingBottom: '15px' }}>
          <DynamicTableCSVReport
            tableData={data}
            tableName={'OperatorPerformanceNFAI'}
            name={'OperatorPerformanceNFAI'}
            fileName={`StartDate_${startDate}_EndDate_${endDate}_OperatorPerformanceReport`}
          />
        </div>
      )}

      {/* Loading/Error */}
              {loading && (
          <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingTop: '40px', // space from top
              height: '100vh', // or adjust as per container
            }}>
            <style>
              {`
                @keyframes spinLoader {
                  100% { transform: rotate(1turn); }
                }
              `}
            </style>
            <div style={styles.loader as any}></div>
          </div>
        )}
      {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

      {/* Table */}
      {!loading && data.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#00796b', color: 'white' }}>
            <tr>
              <th  style={{ padding: '10px', border: '1px solid #ccc' }}>S.No</th>
              {Object.keys(data[0]).map((col, i) => (
                <th key={i} style={{ padding: '10px', border: '1px solid #ccc' }}>
                  {col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                style={{
                  backgroundColor: rowIdx % 2 === 0 ? '#f9f9f9' : '#ffffff' }}>
                    <td style={{ padding: "8px", border: "1px solid #ccc" }}>
                  {rowIdx + 1}
                </td>
                {Object.keys(row).map((col, colIdx) => (
                  <td
                    key={colIdx}
                    style={{ padding: "8px", border: "1px solid #ccc" }}
                  >
                    {row[col as keyof DataItem]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && data.length === 0 && (
        <p style={{ textAlign: 'center', marginTop: '20px' }}>No data found.</p>
      )}
    </div>
  );
};

export default observer(OperatorPerformanceNFAIReport);
